import { create } from 'zustand';

/**
 * Cookie-consent store (client-side, GDPR strict opt-in).
 *
 * We only set non-essential (analytics) cookies AFTER the visitor explicitly
 * accepts — GA is never loaded until `consent.analytics === true`. The decision
 * is persisted in localStorage and version-stamped so a future change to what we
 * collect can re-prompt everyone by bumping CONSENT_VERSION.
 *
 * `necessary` is always true (auth/session/theme + this consent record itself —
 * strictly required, exempt from consent under ePrivacy). We model it explicitly
 * so the preferences UI can show it as a locked-on row.
 *
 * Two consumers react to this store: PublicAnalytics (loads/unloads GA) and the
 * CookieConsentBanner (shows the banner / preferences panel). The PublicFooter's
 * "Cookie settings" link calls `openSettings()` to re-open the panel after a
 * decision has been made.
 */
const CONSENT_KEY = 'sg_cookie_consent';
const CONSENT_VERSION = 1;

export interface CookieConsent {
  /** Strictly-necessary cookies — always on, cannot be declined. */
  necessary: true;
  /** Analytics (Google Analytics). The only optional category today. */
  analytics: boolean;
  /** Schema version of the stored decision; mismatches are treated as "no decision". */
  version: number;
  /** ISO timestamp of the decision. */
  consentedAt: string;
}

function read(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsent;
    // Re-prompt if the stored decision predates the current consent schema.
    if (parsed.version !== CONSENT_VERSION) return null;
    return { ...parsed, necessary: true };
  } catch {
    return null;
  }
}

function write(analytics: boolean): CookieConsent {
  const consent: CookieConsent = {
    necessary: true,
    analytics,
    version: CONSENT_VERSION,
    consentedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
  } catch {
    /* private mode / storage disabled — consent is best-effort, defaults to off */
  }
  return consent;
}

interface ConsentStore {
  /** Persisted decision, or null until the visitor chooses. */
  consent: CookieConsent | null;
  /** Whether the preferences panel was explicitly re-opened (after a prior decision). */
  settingsOpen: boolean;
  /** Accept all optional categories (analytics on). */
  acceptAll: () => void;
  /** Reject all optional categories (analytics off). */
  rejectAll: () => void;
  /** Save a granular choice from the preferences panel. */
  save: (analytics: boolean) => void;
  /** Re-open the preferences panel (footer "Cookie settings"). */
  openSettings: () => void;
  /** Close the preferences panel without changing the stored decision. */
  closeSettings: () => void;
}

export const useCookieConsent = create<ConsentStore>((set) => ({
  consent: read(),
  settingsOpen: false,
  acceptAll: () => set({ consent: write(true), settingsOpen: false }),
  rejectAll: () => set({ consent: write(false), settingsOpen: false }),
  save: (analytics: boolean) => set({ consent: write(analytics), settingsOpen: false }),
  openSettings: () => set({ settingsOpen: true }),
  closeSettings: () => set({ settingsOpen: false }),
}));
